import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// ── Charte Arcus ──────────────────────────────────────────────
const ORANGE = '#F18A00';
const RED    = '#DC2626';
const BORDER = '#E5E7EB';
const TEXT   = '#161616';
const MUTED  = '#64748B';

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

/**
 * Modal de confirmation de suppression "God Mode" pour le rôle IT.
 * Affiche le compteur des entités liées qui seront supprimées en cascade.
 *
 * Props:
 *   - open          : bool — afficher / cacher
 *   - onClose       : () => void — fermer le modal
 *   - onConfirmed   : () => void — callback après suppression réussie
 *   - entityType    : 'client' | 'police' | 'intermediaire' | 'prestataire'
 *   - entityId      : UUID de l'entité à supprimer
 *   - entityLabel   : nom affichable (ex. "The travelers", "RCP2026-003")
 */
const ENDPOINT_MAP = {
  client:        'clients',
  police:        'polices',
  intermediaire: 'intermediaires',
  prestataire:   'prestataires',
};
function endpointFor(entityType, id, suffix = '') {
  const seg = ENDPOINT_MAP[entityType] || entityType;
  return `/api/${seg}/${id}${suffix}`;
}

export default function DeleteCascadeModal({ open, onClose, onConfirmed, entityType, entityId, entityLabel }) {
  const { t } = useLanguage();
  const [impact, setImpact]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDel]    = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!open || !entityId) return;
    setError('');
    setImpact(null);
    setLoading(true);
    const endpoint = endpointFor(entityType, entityId, '/delete-impact');
    authFetch(endpoint)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setImpact)
      .catch(() => setError(t('it_delete.impact_error')))
      .finally(() => setLoading(false));
  }, [open, entityId, entityType, t]);

  const doDelete = async () => {
    setDel(true);
    setError('');
    try {
      const endpoint = endpointFor(entityType, entityId);
      const r = await authFetch(endpoint, { method: 'DELETE' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur');
      }
      onConfirmed?.();
      onClose();
    } catch (e) {
      setError(e.message || t('it_delete.delete_error'));
    } finally {
      setDel(false);
    }
  };

  if (!open) return null;

  // Format du compteur en fonction du type d'entité
  const renderImpact = () => {
    if (loading) return <div style={{ color: MUTED, fontSize: 13 }}>{t('common.loading')}</div>;
    if (!impact) return null;
    const items = [];
    if (entityType === 'client') {
      items.push({ k: 'polices',         label: t('it_delete.polices'),         n: impact.polices });
      items.push({ k: 'amendements',     label: t('it_delete.amendements'),     n: impact.amendements });
    } else if (entityType === 'police') {
      items.push({ k: 'amendements',     label: t('it_delete.amendements'),     n: impact.amendements });
      items.push({ k: 'renouvellements', label: t('it_delete.renouvellements'), n: impact.renouvellements });
    } else if (entityType === 'intermediaire') {
      items.push({ k: 'clients',         label: t('it_delete.clients_unlinked'), n: impact.clients });
    } else if (entityType === 'prestataire') {
      items.push({ k: 'dossiers',        label: t('it_delete.dossiers_linked'),  n: impact.dossiers });
    }
    const total = items.reduce((s, i) => s + i.n, 0);
    if (total === 0) {
      return (
        <div style={{ fontSize: 13, color: MUTED, padding: '12px 14px', background: '#F8FAFC', borderRadius: 8 }}>
          {t('it_delete.no_cascade')}
        </div>
      );
    }
    return (
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: RED, letterSpacing: '.04em', marginBottom: 8 }}>
          {t('it_delete.also_deleted')}
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.filter(i => i.n > 0).map(i => (
            <li key={i.k} style={{ fontSize: 13, color: '#7F1D1D', display: 'flex', justifyContent: 'space-between' }}>
              <span>{i.label}</span>
              <strong style={{ marginLeft: 12 }}>{i.n}</strong>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, width: 'min(460px, calc(100% - 32px))',
        padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#FEE2E2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01"/>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: TEXT, marginBottom: 4 }}>
              {t('it_delete.title')}
            </div>
            <div style={{ fontSize: 12, color: MUTED, letterSpacing: '.04em', textTransform: 'uppercase' }}>
              {t('it_delete.god_mode')} · {t(`it_delete.entity_${entityType}`)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16, fontSize: 14, color: TEXT, lineHeight: 1.5 }}>
          {t('it_delete.about_to_delete')} <strong>{entityLabel}</strong>.
        </div>

        <div style={{ marginBottom: 16 }}>
          {renderImpact()}
        </div>

        <div style={{ fontSize: 12, color: MUTED, marginBottom: 20, padding: '8px 12px', background: '#F8FAFC', borderRadius: 6 }}>
          ⚠ {t('it_delete.irreversible')}
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#7F1D1D', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={deleting}
            style={{
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${BORDER}`,
              background: '#fff', color: TEXT, fontWeight: 500, fontSize: 14, cursor: deleting ? 'not-allowed' : 'pointer',
            }}>
            {t('common.cancel')}
          </button>
          <button onClick={doDelete} disabled={deleting || loading}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: RED, color: '#fff', fontWeight: 600, fontSize: 14,
              cursor: (deleting || loading) ? 'not-allowed' : 'pointer',
              opacity: (deleting || loading) ? .7 : 1,
            }}>
            {deleting ? t('common.deleting') : t('it_delete.confirm_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}
